/**
 * @file        src/pages/account/PaymentReconciliation.jsx
 * @description Account Management → Payment Reconciliation. Every payment where
 *              the money and the delivery disagree: the gateway captured and no
 *              booking was placed (a refund is owed), or a confirmed booking
 *              never collected its wallet leg (money is owed to US).
 *
 *              booking_backend has written this table since 2026-07-20 and
 *              NOTHING read it but two CLI scripts, so a refund owed to a
 *              customer was findable only by someone who already knew to look.
 *
 *              TWO DIRECTIONS OF MONEY, and the screen never merges them. The
 *              KPI strip reports them as two totals — adding "owed to them" to
 *              "owed to us" produces a number that means nothing and understates
 *              both — and every row carries its direction as a chip, because
 *              `wallet_capture_failed` looks exactly like a refund until you
 *              read the reason, and refunding one pays out money we never took.
 *
 *              RECORD-ONLY (client decision 2026-09-22). No gateway refund API
 *              exists in this codebase: the refund is made in the Easebuzz
 *              dashboard and recorded here. The screen says so in as many
 *              words rather than implying a button moves money.
 *
 *              WHOLE-BUSINESS, not split B2C / Corporate (§9d) — same exception
 *              as Transaction Log and Abandoned Payments. Many rows are guest
 *              checkouts with no user at all.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-22 Claude — Initial (money-discrepancy queue).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import {
  listPaymentReconciliation,
  paymentReconciliationTotals,
  paymentReconciliationReasons,
  getPaymentReconciliation,
  settlePaymentReconciliation,
} from '../../api/paymentReconciliation';
import { inr, formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';

const FLOW_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'ssr', label: 'Add-ons' },
  { value: 'reissue', label: 'Reschedule' },
];
const FLOW_LABEL = {
  flight: 'Flight', hotel: 'Hotel', insurance: 'Insurance',
  ssr: 'Add-ons', reissue: 'Reschedule',
};

const DIRECTION_OPTIONS = [
  { value: '', label: 'Both directions' },
  { value: 'refund_due', label: 'Refund owed to customer' },
  { value: 'owed_to_us', label: 'Owed to us' },
];
const DIRECTION_LABEL = {
  refund_due: 'Refund owed',
  owed_to_us: 'Owed to us',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'resolved', label: 'Closed — nothing owed' },
];
const STATUS_LABEL = {
  open: 'Open', refunded: 'Refunded', resolved: 'Closed',
};
const STATUS_TONE = { open: 'warn', refunded: 'ok', resolved: 'pend' };

/**
 * What each reason MEANS, in the words of someone who has to act on it. An
 * unrecognised reason is shown AS STORED — a new one must be visible the day
 * booking_backend first writes it, not disguised as one we already know. Same
 * rule as Abandoned Payments' gateway verdicts.
 */
const REASON_COPY = {
  payment_received_context_lost: {
    label: 'Captured, booking never placed',
    detail: 'The gateway took the money but the checkout could not be claimed — no booking exists. Refund the full amount.',
  },
  context_expired: {
    label: 'Captured after checkout expired',
    detail: 'The payment landed after the checkout had timed out, so no booking was placed. Refund the full amount.',
  },
  booking_failed_after_capture: {
    label: 'Airline refused after payment',
    detail: 'Money captured, the supplier rejected the booking. No ticket exists. Refund the full amount.',
  },
  hotel_rejected_after_capture: {
    label: 'Hotel refused after payment',
    detail: 'Money captured, the room was gone by the time we booked (typically "no longer available"). No stay exists. Refund the full amount.',
  },
  ssr_not_provisioned: {
    label: 'Add-ons paid for, not provisioned',
    detail: 'The BOOKING IS LIVE and correct — only the seats/meals/baggage below were not added. Refund this amount only, never the booking total.',
  },
  wallet_capture_failed: {
    label: 'Wallet leg never collected',
    detail: 'The booking is confirmed and the customer owes US this amount — their wallet was not debited. DO NOT REFUND. Collect or write off.',
  },
};
const reasonLabel = (r) => REASON_COPY[r]?.label || r || '—';

function Field({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children || <span className="muted">—</span>}</div></div>
  );
}

/** How long money has been sitting unsettled, and how alarmed to be about it. */
function AgeChip({ days, status }) {
  if (status !== 'open') return <span className="muted">—</span>;
  const d = Number(days) || 0;
  const tone = d >= 14 ? 'rej' : d >= 7 ? 'warn' : 'pend';
  return <Chip tone={tone}>{d === 0 ? 'Today' : `${d}d`}</Chip>;
}

/**
 * The item in full, plus the settlement form. Fed from the list row — the list
 * endpoint returns every column except the raw callback, which is why the modal
 * fetches the single item for that one panel.
 */
function SettleModal({ row, brandLabel, onClose }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(row?.status || 'open');
  const [note, setNote] = useState(row?.note || '');
  const [password, setPassword] = useState('');

  // The list payload carries every column EXCEPT the raw callback body — that
  // is a whole gateway POST per row and belongs to one item at a time. For a
  // captured-but-never-booked item it is the only surviving record of who paid,
  // so the panel is worth a second request; until it lands the rest of the
  // modal renders from the list row.
  const { data: fullRes } = useQuery({
    queryKey: ['payment-reconciliation', 'item', row?.id],
    queryFn: () => getPaymentReconciliation(row.id),
    enabled: Boolean(row?.id),
  });
  const gatewayPost = fullRes?.data?.gateway_post;

  const owedToUs = row?.direction === 'owed_to_us';
  // Only this one claims money moved, and only this one asks for the password.
  const needsPassword = status === 'refunded';

  const mut = useMutation({
    mutationFn: () => settlePaymentReconciliation(row.id, {
      status,
      note,
      ...(needsPassword ? { password } : {}),
    }),
    onSuccess: () => {
      toast.ok('Saved', 'Settlement recorded.');
      qc.invalidateQueries({ queryKey: ['payment-reconciliation'] });
      onClose();
    },
    onError: (e) => toast.err(
      'Could not save',
      e.response?.data?.error?.message || 'Please try again.',
    ),
  });

  if (!row) return null;
  const copy = REASON_COPY[row.reason];

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass">
        <div className="lk-modal-head">
          <h3>{FLOW_LABEL[row.flow] || row.flow} · {inr(row.amount)}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          {/* The direction, stated before anything else. Everything below reads
              differently depending on it, and the expensive mistake this screen
              can cause is refunding a row that owes money the other way. */}
          <h4 className="det-h">What is owed</h4>
          <div className="det-grid">
            <Field label="Direction">
              <Chip tone={owedToUs ? 'rej' : 'warn'}>{DIRECTION_LABEL[row.direction]}</Chip>
            </Field>
            <Field label="Amount">{inr(row.amount)}</Field>
            <Field label="Cause">{reasonLabel(row.reason)}</Field>
            <Field label="Outstanding">
              <AgeChip days={row.age_days} status={row.status} />
            </Field>
          </div>
          {copy ? <p className="muted">{copy.detail}</p> : (
            // A reason this console has never seen. Say so plainly instead of
            // rendering a blank explanation that reads as "nothing special".
            <p className="muted">
              Cause recorded by the booking engine as <span className="mono">{row.reason}</span> —
              no guidance for this one yet. Check the raw callback below before acting.
            </p>
          )}

          <h4 className="det-h">Customer</h4>
          <div className="det-grid">
            <Field label="Name">{row.customer_name}</Field>
            <Field label="Email">
              {row.customer_email ? <a href={`mailto:${row.customer_email}`}>{row.customer_email}</a> : null}
            </Field>
            <Field label="Phone">
              {row.customer_phone ? <a href={`tel:${row.customer_phone}`}>{row.customer_phone}</a> : null}
            </Field>
            <Field label="Brand">{brandLabel(row.brand)}</Field>
          </div>

          <h4 className="det-h">The payment</h4>
          <div className="det-grid">
            <Field label="Transaction"><span className="mono">{row.txnid}</span></Field>
            <Field label="Product">{FLOW_LABEL[row.flow] || row.flow}</Field>
            <Field label="Method">{row.payment_method}</Field>
            {/* Present only when a payments row exists — that is, when a booking
                was placed. Its absence is itself the diagnosis for the
                captured-but-never-booked reasons. */}
            <Field label="Captured">
              {row.captured_amount != null ? inr(row.captured_amount) : null}
            </Field>
            <Field label="Booking">
              {row.booking_id ? <span className="mono">#{row.booking_id}</span> : null}
            </Field>
            <Field label="Flagged">{formatDateTime(row.created_at)}</Field>
          </div>

          <h4 className="det-h">Settlement</h4>
          {/* Said plainly: nothing on this screen moves money. */}
          <p className="muted">
            {owedToUs
              ? 'Recovered or written off elsewhere — record the outcome here. Nothing on this screen moves money.'
              : 'Issue the refund in the Easebuzz dashboard, then record it here. Nothing on this screen moves money.'}
          </p>
          <div className="det-grid">
            <Field label="Last handled by">{row.handled_by}</Field>
            <Field label="Last handled">{row.handled_at ? formatDateTime(row.handled_at) : null}</Field>
          </div>
          <div className="field">
            <label>Status</label>
            <Select
              value={status}
              onChange={(v) => { setStatus(v); setPassword(''); }}
              options={[
                { value: 'open', label: 'Open — still outstanding' },
                { value: 'refunded', label: owedToUs ? 'Settled — money collected' : 'Refunded — money returned' },
                { value: 'resolved', label: 'Closed — nothing owed' },
              ]}
            />
          </div>
          <div className="field">
            <label>Note — what happened?</label>
            <textarea
              value={note}
              maxLength={500}
              placeholder={owedToUs
                ? 'e.g. collected on the next booking · written off, below threshold'
                : 'e.g. refunded in Easebuzz ref RFD12345 on 22 Sep · customer rebooked instead'}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {needsPassword ? (
            <div className="field">
              <label>Your password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Confirm it was actually settled"
              />
              <small className="muted">
                Recording a settlement asserts that money moved, so it is confirmed
                the same way as a wallet credit.
              </small>
            </div>
          ) : null}
          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn primary"
              disabled={mut.isPending || (needsPassword && !password)}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>

          {gatewayPost ? (
            <>
              <h4 className="det-h">Gateway callback</h4>
              {/* Scrolls in its own box so a long payload never widens the
                  modal or reflows the fields above it. */}
              <pre className="mono" style={{ maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {typeof gatewayPost === 'string'
                  ? gatewayPost
                  : JSON.stringify(gatewayPost, null, 2)}
              </pre>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function PaymentReconciliation() {
  const { options: brandOptions, isLoading: brandsLoading, labelOf } = useBrands();

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [direction, setDirection] = useState('');
  const [flow, setFlow] = useState('');
  // Defaults to the working queue, not the archive — this is a worklist.
  const [status, setStatus] = useState('open');
  const [reason, setReason] = useState('');
  const [brand, setBrand] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailId, setDetailId] = useState(null);

  const { data: reasonsRes } = useQuery({
    queryKey: qk.paymentReconciliation.reasons(),
    queryFn: paymentReconciliationReasons,
  });
  // Only the reasons actually present, so the filter never offers a dead option
  // — and a brand-new reason appears here on its own.
  const reasonOptions = [
    { value: '', label: 'All causes' },
    ...(reasonsRes?.data?.reasons || []).map((r) => ({ value: r, label: reasonLabel(r) })),
  ];

  const brandFilterOptions = [{ value: '', label: 'All brands' }, ...brandOptions];
  const brandLabel = (b) => (b === 'unknown' || b == null ? 'Unknown' : labelOf(b));

  // ONE filter object for the table and the KPI strip, so a card can never
  // disagree with the rows beneath it (§9d). Only `page` differs.
  const filters = {
    ...(direction ? { direction } : {}),
    ...(flow ? { flow } : {}),
    ...(status ? { status } : {}),
    ...(reason ? { reason } : {}),
    ...(brand ? { brand } : {}),
    ...(appliedSearch ? { search: appliedSearch } : {}),
    ...(from ? { from } : {}),
    // Inclusive end-of-day — a bare date compares as 00:00:00 and would
    // silently drop every item flagged on the "to" day itself.
    ...(to ? { to: `${to} 23:59:59.999` } : {}),
  };
  const listParams = { ...filters, page, pageSize };

  const { data: list, isLoading } = useQuery({
    queryKey: qk.paymentReconciliation.list(listParams),
    queryFn: () => listPaymentReconciliation(listParams),
    placeholderData: keepPreviousData,
  });

  const { data: totalsRes } = useQuery({
    queryKey: qk.paymentReconciliation.totals(filters),
    queryFn: () => paymentReconciliationTotals(filters),
  });

  const body = list?.data || {};
  const rows = body.rows || [];
  const total = body.total || 0;
  const t = totalsRes?.data;
  const detailRow = rows.find((r) => r.id === detailId) || null;

  function onSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }

  const applyFilter = (setter) => (v) => { setter(v); setPage(1); };

  return (
    <>
      <PageHead
        title="Payment Reconciliation"
        subtitle="Account Management · payments where the money and the delivery disagree — refunds we owe, and wallet money we never collected"
      />

      {/* TWO totals, never one. The queue points both ways and summing it would
          understate both sides. */}
      <div className="mini-kpi">
        <article className="mk glass neo">
          <small>Refunds Owed</small>
          <b className="deb">{t ? `${t.open_refunds} · ${inr(t.open_refund_value)}` : '—'}</b>
        </article>
        <article className="mk glass neo">
          {/* Rose like Refunds Owed, not green: the colour here means "still
              outstanding", not which way the money points — the label and the
              per-row chip already say that. Only Settled is a good number. */}
          <small>Owed To Us</small>
          <b className="deb">{t ? `${t.open_owed_to_us} · ${inr(t.open_owed_to_us_value)}` : '—'}</b>
        </article>
        <article className="mk glass neo">
          {/* The number that says whether anyone is actually working the queue. */}
          <small>Oldest Open</small>
          <b>{t ? (t.open_items ? `${t.oldest_open_days}d` : '—') : '—'}</b>
        </article>
        <article className="mk glass neo">
          <small>Settled</small>
          <b className="cre">{t ? `${t.refunded} · ${inr(t.refunded_value)}` : '—'}</b>
        </article>
      </div>

      <article className="panel glass">
        <form className="search-row" onSubmit={onSearchSubmit}>
          <div className="field">
            <label>Transaction / Email</label>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Direction</label>
            <Select value={direction} onChange={applyFilter(setDirection)} options={DIRECTION_OPTIONS} />
          </div>
          <div className="field">
            <label>Cause</label>
            <Select value={reason} onChange={applyFilter(setReason)} options={reasonOptions} />
          </div>
          <div className="field">
            <label>Product</label>
            <Select value={flow} onChange={applyFilter(setFlow)} options={FLOW_OPTIONS} />
          </div>
          <div className="field">
            <label>Status</label>
            <Select value={status} onChange={applyFilter(setStatus)} options={STATUS_OPTIONS} />
          </div>
          <div className="field">
            <label>Brand</label>
            <Select
              value={brand}
              onChange={applyFilter(setBrand)}
              options={brandFilterOptions}
              placeholder={brandsLoading ? 'Loading…' : 'All brands'}
            />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Flagged</th><th>Age</th><th>Direction</th><th>Cause</th>
                  <th>Product</th><th>Customer</th><th>Amount</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="empty">Nothing outstanding for these filters.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{formatDateTime(r.created_at)}</td>
                    <td><AgeChip days={r.age_days} status={r.status} /></td>
                    <td>
                      <Chip tone={r.direction === 'owed_to_us' ? 'rej' : 'warn'}>
                        {DIRECTION_LABEL[r.direction]}
                      </Chip>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <div
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={REASON_COPY[r.reason]?.detail || r.reason}
                      >
                        {reasonLabel(r.reason)}
                      </div>
                      <small className="muted mono">{r.txnid}</small>
                    </td>
                    <td>
                      <div>{FLOW_LABEL[r.flow] || r.flow}</div>
                      <small className="muted">{brandLabel(r.brand)}</small>
                    </td>
                    <td>
                      <div>{r.customer_name || <span className="muted">—</span>}</div>
                      {r.customer_email ? (
                        <small><a href={`mailto:${r.customer_email}`}>{r.customer_email}</a></small>
                      ) : null}
                    </td>
                    <td>{inr(r.amount)}</td>
                    <td>
                      <Chip tone={STATUS_TONE[r.status] || 'pend'}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Chip>
                      {r.note ? (
                        <div>
                          <small
                            className="muted"
                            style={{ display: 'inline-block', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.note}
                          >
                            {r.note}
                          </small>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn xs ghost" onClick={() => setDetailId(r.id)}>
                          <Icon name="search" /> {r.status === 'open' ? 'Settle' : 'Details'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {detailRow ? (
        <SettleModal
          // Remounts per item so the status/note form starts from THAT row's
          // stored values instead of keeping the previous one's state.
          key={detailRow.id}
          row={detailRow}
          brandLabel={brandLabel}
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </>
  );
}
